import {FormProvider, useForm} from "react-hook-form";
import {useEffect} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useSearchParams} from "react-router-dom";
import BreadcrumbLayout
  from "@/components/common/CustomLayout/BreadcrumbLayout";
import Button from "@/components/common/Button";
import {
  ReactComponent as RiDeleteBinLine
} from '@/assets/icons/ri-delete-bin-line.svg'
import {ReactComponent as RiEditLine} from "@/assets/icons/ri-edit-line.svg";
import {
  ReactComponent as CalendarCheckLine
} from "@/assets/icons/calendar-check-line.svg";
import {ReactComponent as RiAddLine} from "@/assets/icons/ri-add-line.svg";
import {
  DAY_OF_WEEK, ProgramStatus,
  ProgramStatusColor,
  ProgramType,
  ProgramTypeColor,
  STATUS_FILTER_COLOR_MAPPING
} from "@/pages/Programs/List/constant";
import './index.css'
import Table from "@/components/common/Table";
import {currencyFormat, hourFormat} from "@/utils";
import {RootState, useAppDispatch} from "@/store";
import {
  deleteProgram,
  getPrograms,
  getProgramsSummary
} from "@/store/program-summary/action";
import {getUserVenues} from "@/store/user-info/action";
import { constructTableMetaFromPaging2 } from "@/utils/paging";
import useCustomPaging from "@/hooks/useCustomPaging";
import {dynamicAttr} from "@/utils/fallback";
import useUserVenueSelectFilter from "@/hooks/useUserVenueSelectFilter";
import useSearchFilter from "@/hooks/useSearchFilter";
import UserVenueSelect from "@/components/common/UserVenueSelect";
import GenericSelect from "@/components/common/GenericSelect";

const renderPeriodDetailsRecurring = (details: ProgramDetails[]) => (
  <div className="flex flex-col flex-nowrap whitespace-nowrap gap-1">
    {details.map(d => (
      <div key={d.id}
           className="flex justify-start item gap-2">
        <CalendarCheckLine/>
        <div className="text-jxs font-medium">{DAY_OF_WEEK[d.day]}, {hourFormat(d.start_time)}</div>
      </div>
    ))}
  </div>
)
const renderPeriodDetailsOneTime = (details: ProgramDetails[]) => (
  <div className="flex flex-col flex-nowrap whitespace-nowrap gap-1">
    {details.map(d => (
      <div key={d.id}
           className="flex justify-start item gap-2">
        <CalendarCheckLine/>
        <div className="text-jxs font-medium">{new Date(d.start_date).toLocaleDateString()}, {hourFormat(d.start_time)}</div>
      </div>
    ))}
  </div>
)
const renderInfo = (info: ProgramSummaryRowInfo) => {
  const imgSource = info.img.includes('http')
    ? info.img
    : `https://${info.img}`
  return (
    <div className="flex justify-start items-start gap-4">
      <img
        className="object-cover w-[100px] h-[50px]"
        src={imgSource}
        alt={info.img}/>
      <div className="text-jsm font-bold">
        {info.title}
      </div>
    </div>
  )
}
const renderPrice = (price: number) => (
  <div>
    {currencyFormat(price)}
  </div>
)
const renderStatus = (status: string) => (
  <div className={`flex items-center justify-center font-medium py-1 px-4 rounded ${ProgramStatusColor[status].background} ${ProgramStatusColor[status].text}`}>
    {status}
  </div>
)
const renderPeriod = (period: ProgramSummaryRowPeriod) => {
  const endDate = Math.max(...period.details.map(d => d.end_date))
  return (
    <div className="flex flex-col justify-start items-start">
      <div className={`text-jsm font-bold mb-1 ${ProgramTypeColor[period.type]}`}>
        {period.type.replace('_', ' ')}
      </div>
      {
        period.type === ProgramType.RECURRING
          ? renderPeriodDetailsRecurring(period.details)
          : renderPeriodDetailsOneTime(period.details)
      }
      {
        period.type === ProgramType.RECURRING &&
        <div className="flex flex-col justify-start items-start mt-3">
          <div className="text-jm-sm text-grey-dark">
            End Date
          </div>
          <div className="text-xs font-medium">
            {new Date(endDate).toLocaleDateString()}
          </div>
        </div>
      }
    </div>
  )
}
const renderAction = (programId: string, removeProgram: Function, editProgram: Function) => (
  <div className="flex justify-start items-start gap-5">
    <Button
      onClick={() => editProgram(programId)}
      type={null}
      variant="secondary"
      className="border-stroke-subtle border px-4"
    >
      <div className="flex gap-2 justify-center items-center h-4">
        <RiEditLine/>
        Edit
      </div>
    </Button>
    <Button
      onClick={() => {
        removeProgram(programId)
      }}
      type={null}
      variant="secondary"
      className="border-stroke-subtle border px-4"
    >
      <div className="flex gap-2 justify-center items-center h-4">
        <RiDeleteBinLine/>
      </div>
    </Button>
  </div>
)

const constructProgramRow = (program: Program) => ({
    id: program.id,
    info: {
      img: program.thumbnails?.[0] ?? '',
      title: program.title
    },
    venueName: program.venue.name,
    price: program.price,
    quota: program.quota,
    period: {
      details: program.details,
      type: program.type
    },
    status: program.status
  }
)
const constructTableBodyData = (programs: Program[]) => programs.map(p => ({
  id: p.id,
  value: constructProgramRow(p)
}))
const constructTableData = (programs: Program[], removeProgram: Function, editProgram: Function) => ({
  data: constructTableBodyData(programs),
  component: {
    info: renderInfo,
    price: renderPrice,
    status: renderStatus,
    period: renderPeriod,
    id: (id) => renderAction(id, removeProgram, editProgram)
  }
})

const HEADERS: TableHeader[] = [
  {
    label: 'Judul',
    key: 'info'
  },
  {
    label: 'Venue',
    key: 'venueName'
  },
  {
    label: 'Price',
    key: 'price'
  },
  {
    label: 'Quota',
    key: 'quota'
  },
  {
    label: 'Periode',
    key: 'period'
  },
  {
    label: 'Status',
    key: 'status'
  },
  {
    label: 'Action',
    key: 'id'
  }
]
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {
  }
}

const renderStatusFilter = (statuses: ProgramStatusFilter[], onClick: Function, current) => (
  <div className="flex justify-start items-center whitespace-nowrap">
    {statuses.map(status => (
      <Button
        type='button'
        variant='primary'
        key={status.key}
        className={
          `border border-grey-line-subtle rounded-none gap-3
        ${current === status.key ? STATUS_FILTER_COLOR_MAPPING.ACTIVE.button : STATUS_FILTER_COLOR_MAPPING.INACTIVE.button}
        `
        }
        onClick={() => {
          onClick(status.key)
        }}>
        {status.label}
        <div
          className={`
            flex py-0 px-2 gap-3 rounded-full
            ${current === status.key ? STATUS_FILTER_COLOR_MAPPING.ACTIVE.count : STATUS_FILTER_COLOR_MAPPING.INACTIVE.count}
          `}>
          {status.count}
        </div>
      </Button>)
    )}
  </div>
)

const ProgramSummary = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const summaryFilterMethods = useForm()
  const {
    programs,
    summary,
    paging
  } = useSelector((rootState: RootState) => rootState.programSummary.state)
  
  const [searchParams] = useSearchParams()
  const [
    currentPageFilter,
    changeFilterWithoutPage,
    handlePageNumberChange
  ] = useCustomPaging()
  const [
    currentVenueId,
    selectedVenue,
    venueOptions,
    handleChangeVenue,
  ] = useUserVenueSelectFilter()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()

  const currentStatus = searchParams.get('status') ?? ''
  const currentType = searchParams.get('type') ?? ''

  const typeFilters = [
    {
      key: '',
      label: 'All Periode',
      count: summary.all
    },
    {
      key: 'ONE_TIME',
      label: 'One Time',
      count: summary.one_time
    },
    {
      key: 'RECURRING',
      label: 'Recurring',
      count: summary.recurring
    }
  ]
  const statusFilter = [
    {
      value: '',
      label: 'Semua Status'
    },
    {
      value: ProgramStatus.DRAFT,
      label: ProgramStatus.DRAFT
    },
    {
      value: ProgramStatus.PUBLISHED,
      label: ProgramStatus.PUBLISHED
    },
    {
      value: ProgramStatus.ARCHIVED,
      label: ProgramStatus.ARCHIVED
    }
  ]
  
  const handleChangeType = (status: string) => {
    changeFilterWithoutPage('type', status)
  }
  
  const getSelectedStatusOption = () => statusFilter.map(s => ({
    label: s.label,
    value: s.value
  }))
  const handleChangeStatus = (val) => {
    changeFilterWithoutPage('status', val.value)
  }
  const getSelectedStatus = () => currentStatus
    ? { value: currentStatus, label: currentStatus }
    : { value: '',  label: 'Semua Status' }
  
  const removeProgram = async (id: string) => {
    await dispatch(deleteProgram(id))
  }
  const handleClickAdd = () => {
    navigate('/dashboard/programs/add')
  }
  const handleClickEdit = (id) => {
    navigate(`/dashboard/programs/edit/${id}`)
  }
  const baseParams = () => ({
    size: 10,
    ...dynamicAttr('page', currentPageFilter),
    ...dynamicAttr('status', currentStatus),
    ...dynamicAttr('search', currentSearchFilter),
    ...dynamicAttr('venueId', currentVenueId)
  })

  const getProgramsData = () => {
    const params = {
      ...baseParams(),
      ...dynamicAttr('type', currentType),
    }
    dispatch(getPrograms(params))
  }

  const getProgramSummary = () => {
    dispatch(getProgramsSummary(baseParams()))
  }
  
  useEffect(() => {
    getProgramsData()
    getProgramSummary()
    dispatch(getUserVenues({}))
  }, [])
  
  useEffect(() => {
    getProgramsData()
    getProgramSummary()
  }, [
    currentType,
    currentSearchFilter,
    currentVenueId,
    currentStatus,
    currentPageFilter
  ])
  
  return (
    <FormProvider {...summaryFilterMethods}>
      <BreadcrumbLayout>
        <div className="flex flex-col justify-center items-center w-full gap-7">
          <div className="flex w-full justify-between items-center">
            <div className="text-j2xl font-bold">
              Programs
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-start p-0 w-full gap-2 h-full">
                <input
                  value={searchString}
                  onChange={handleChangeSearchString}
                  onKeyDown={handleSubmitSearch}
                  name="searchString"
                  type="text"
                  className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
                  placeholder="Search for Programs"/>
              </div>
              <div className="separator"/>
              <Button
                onClick={handleClickAdd}
                type={null}
                variant="primary"
                className="whitespace-nowrap flex gap-2 pl-4 py-2 justify-center items-center">
                <div className="w-[18px]">
                  <RiAddLine/>
                </div>
                <span className="text-jsm">Program</span>
              </Button>
            </div>
          </div>
          <div className="flex flex-col justify-center items-center w-full bg-white drop-shadow rounded-md">
            <div className="flex justify-start items-center gap-4 py-4 px-6 w-full">
              {renderStatusFilter(typeFilters, handleChangeType, currentType)}
              <div className="separator"/>
              <div>
                <UserVenueSelect
                  useIcon={false}
                  name="venueId"
                  id="venueId"
                  placeholder="Select Venue"
                  options={venueOptions}
                  value={selectedVenue}
                  onChange={handleChangeVenue}
                />
              </div>
              <div className="separator"/>
              <div>
                <GenericSelect
                  name="status"
                  id="status"
                  placeholder="Pilih Status"
                  options={getSelectedStatusOption()}
                  value={getSelectedStatus()}
                  onChange={handleChangeStatus}
                />
              </div>
              <div className="flex justify-end items-center p-0 gap-2">
                {/* TODO: To dropdown and do actual pagination */}
              </div>
              <div className="flex justify-end items-center flex-grow whitespace-nowrap">
                10 item per page
              </div>
            </div>
            <div className="w-full">
              <Table
                headers={HEADERS}
                body={constructTableData(programs, removeProgram, handleClickEdit)}
                sort={SORT}
                meta={constructTableMetaFromPaging2(paging)}
                pageNumberHandler={handlePageNumberChange}
              />
            </div>
          </div>
        </div>
      </BreadcrumbLayout>
    </FormProvider>
  )
  
}
export default ProgramSummary;
